import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { defaults, get, map } from 'lodash-es';
import { venomHistory } from 'client/utils/history/venom-history';
import { connectToModel, bindToPath } from 'client/data/luckdragon/redux/react-binding';
import { VehicleModel, buildSubmodelYearsPath } from 'client/data/models/vehicle';
import { generateTrackingIds, LINKS_MAP, DEFAULT_PUB_STATES, LINKS } from 'site-modules/shared/utils/make-model-zip';
import { isUsed } from 'site-modules/shared/utils/publication-states';
import { MakeModelZipDropdownForm } from 'site-modules/shared/components/make-model-zip-dropdown-form/make-model-zip-dropdown-form';

function createParams(make, model, year) {
  if (make && model && year) {
    return {
      make: make.slug,
      model: model.slug,
      year: year.value,
    };
  }

  return make && model ? { make: make.slug, model: model.slug, inventorytype: 'new' } : null;
}

function createHref(linkType, data) {
  const { make, model, year, latestNonPreprodYear, makeModel, params, redirectOldToReview } = data;
  /**
   * Make 'Go' button enabled when Make and Model are selected for SRP_LINK type.
   */
  return !params && linkType === LINKS_MAP.SRP
    ? null
    : LINKS[linkType]({ make, model, year, latestNonPreprodYear, makeModel, params, redirectOldToReview });
}

class MmzSelectionUI extends Component {
  static propTypes = {
    className: PropTypes.string,
    hasZipInput: PropTypes.bool,
    hasYearDropdown: PropTypes.bool,
    pubStates: PropTypes.arrayOf(PropTypes.string),
    tracking: PropTypes.shape({}),
    make: PropTypes.shape({}),
    model: PropTypes.shape({}),
    year: PropTypes.shape({ value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]) }),
    linkType: PropTypes.string,
    rel: PropTypes.string,
    setMake: PropTypes.func.isRequired,
    setModel: PropTypes.func.isRequired,
    setYear: PropTypes.func.isRequired,
    makeModelYears: PropTypes.arrayOf(
      PropTypes.shape({ value: PropTypes.number, isUsed: PropTypes.bool, isPreprod: PropTypes.bool })
    ),
    latestNonPreprodYear: PropTypes.number,
    onSubmit: PropTypes.func,
    isGoBtnDisabled: PropTypes.bool,
    dataTrackingParent: PropTypes.string,
    renderForm: PropTypes.func,
  };

  static defaultProps = {
    className: '',
    hasZipInput: false,
    hasYearDropdown: false,
    pubStates: DEFAULT_PUB_STATES,
    tracking: generateTrackingIds(''),
    make: null,
    model: null,
    year: null,
    linkType: LINKS_MAP.CORE_PAGE,
    rel: '',
    latestYear: null,
    makeModelYears: [],
    latestNonPreprodYear: null,
    onSubmit: null,
    isGoBtnDisabled: false,
    dataTrackingParent: undefined,
    renderForm: undefined,
  };

  state = {
    isZipChangeInProgress: false,
    isSubmitPending: false,
  };

  componentWillUnmount() {
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }

  onSubmit = e => {
    e.preventDefault();
    const { isZipChangeInProgress } = this.state;

    if (this.isHrefValid()) {
      this.setState({ isSubmitPending: true });

      // if the user is currently editing their zip, then the navigation will be handled after the
      // api calls have been made to validate the zip and the zip has been saved to the store/cookie
      if (!isZipChangeInProgress) {
        this.doNavigation();
      }

      this.timeout = setTimeout(() => {
        this.setState({ isSubmitPending: false });
      }, 10000); // 10 seconds - because sometimes the interwebz are just slow
    }
  };

  onZipChangeInProgress = ({ isZipChangeInProgress }) => {
    this.setState({ isZipChangeInProgress }, () => {
      const { isSubmitPending } = this.state;
      if (!isZipChangeInProgress && isSubmitPending) {
        this.doNavigation();
      }
    });
  };

  timeout = null;

  buildHref = () => {
    const { latestNonPreprodYear, make, model, year, linkType, makeModelYears } = this.props;
    const makeModel = make && model && { make: make.slug, model: model.slug };
    const params = createParams(make, model, year);

    return createHref(linkType, {
      make,
      model,
      year,
      latestNonPreprodYear,
      makeModel,
      params,
      makeModelYears,
    });
  };

  isFormDisabled = () => this.props.isGoBtnDisabled || !this.isHrefValid();

  isHrefValid = () => !!this.buildHref();

  doNavigation = () => {
    const { make, model, year, onSubmit } = this.props;

    if (onSubmit) {
      onSubmit({ make, model, year });
    } else {
      venomHistory.push(this.buildHref());
    }
  };

  render() {
    const isFormDisabled = this.isFormDisabled();

    return (
      <MakeModelZipDropdownForm
        {...this.props}
        onSubmit={this.onSubmit}
        onZipChangeInProgress={this.onZipChangeInProgress}
        isFormDisabled={isFormDisabled}
      />
    );
  }
}

const bindToActiveVehiclePath = transform =>
  bindToPath(
    ({ make, model, linkType }) =>
      (linkType === LINKS_MAP.CORE_PAGE || linkType === LINKS_MAP.REVIEW_PAGE || linkType === LINKS_MAP.CPO_SRP) &&
      make &&
      model
        ? buildSubmodelYearsPath({ make: make.slug, model: model.slug })
        : '',
    VehicleModel,
    transform
  );

function getModelYears(submodelYears) {
  let modelYears = {};
  Object.keys(submodelYears).forEach(submodelName => {
    const year = get(submodelYears, `${submodelName}.years`, {});
    modelYears = defaults(modelYears, year);
  });
  return map(
    modelYears,
    (val, key) => key && { value: Number(key), isPreprod: !!val.pubStates.PRE_PROD, isUsed: isUsed(val.pubStates) }
  ).reverse();
}

function getLatestNonPreProdYear(submodelYears) {
  const modelYears = getModelYears(submodelYears);
  const latestNonPreProdYear = modelYears.find(year => !year.isPreprod);
  return latestNonPreProdYear && latestNonPreProdYear.value;
}

const stateToPropsConfig = {
  makeModelYears: bindToActiveVehiclePath(getModelYears),
  latestNonPreprodYear: bindToActiveVehiclePath(getLatestNonPreProdYear),
};

export const MmzSelection = connectToModel(MmzSelectionUI, stateToPropsConfig);
